// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import ProductActions from "@guestsupply/App/Components/Product/ProductActions";
import { createProductDescriptionStyles } from "@guestsupply/App/SharedStyles/productDescriptionStyles";
import { createHorizontalSmallHeadingAndTextStyles } from "@guestsupply/App/SharedStyles/smallHeadingAndTextStyles";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { ProductContext, ProductContextModel } from "@insite/client-framework/Components/ProductContext";
import translate from "@insite/client-framework/Translate";
import { AutocompleteItemModel, ProductAutocompleteItemModel } from "@insite/client-framework/Types/ApiModels";
import SmallHeadingAndText, { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React from "react";
import { css } from "styled-components";
/* eslint-disable spire/fenced-imports */
import { hideAll } from "tippy.js";

interface Props {
    focusedItem?: AutocompleteItemModel;
    goToUrl: (url: string) => void;
    extendedStyles?: AutocompleteProductsStyles;
    // XNG-Changes
    products: ProductContextModel[];
}

export interface AutocompleteProductsStyles {
    headerText?: TypographyPresentationProps;
    container?: GridContainerProps;
    focusedContainer?: GridContainerProps;
    imageGridItem?: GridItemProps;
    image?: LazyImageProps;
    infoGridItem?: GridItemProps;
    titleLink?: LinkPresentationProps;
    erpNumberText?: SmallHeadingAndTextStyles;
    skuAndDescriptionContainer?: InjectableCss;
}

export const autocompleteProductsStyles: AutocompleteProductsStyles = {};

const styles = autocompleteProductsStyles;

class AutocompleteProducts extends React.Component<Props> {
    private readonly styles: AutocompleteProductsStyles;

    constructor(props: Props) {
        super(props);

        this.styles = mergeToNew(styles, props.extendedStyles);
    }

    render() {
        const { products } = this.props;
        const styles = this.styles;
        return (
            <>
                {/* <Typography
                    {...styles.headerText}
                    data-test-selector="autocompleteProducts_Header"
                    className="autocomplete-products-header"
                >
                    {translate("Products")}
                </Typography> */}
                {products.map(productContext => {
                    const {
                        product: { id, productNumber, productTitle, mediumImagePath },
                        productInfo: { productDetailPath },
                    } = productContext;
                    return (
                        <ProductContext.Provider value={productContext} key={id!}>
                            <StyledWrapper {...styles.container} className="autocomplete-products-container">
                                {/* XNG-Changes: making product image clickable */}
                                <Link
                                    href={productDetailPath}
                                    aria-label={`autocomplete product link for ${productTitle}`}
                                    onClick={() => {
                                        hideAll();
                                    }}
                                >
                                    <LazyImage
                                        width="98"
                                        height="101"
                                        {...styles.image}
                                        src={mediumImagePath}
                                        className="autocomplete-products-image"
                                        altText={`autocomplete products for ${productTitle}`}
                                    />
                                </Link>
                                <StyledWrapper
                                    {...styles.infoGridItem}
                                    className="autocomplete-products-info-grid-item"
                                >
                                    <StyledWrapper
                                        className="product-description"
                                        {...styles.skuAndDescriptionContainer}
                                    >
                                        <Link
                                            {...styles.titleLink}
                                            href={productDetailPath}
                                            onClick={() => {
                                                hideAll();
                                            }}
                                            aria-label={productTitle}
                                        >
                                            {productTitle}
                                        </Link>
                                        <SmallHeadingAndText
                                            heading={translate("SKU")}
                                            text={productNumber}
                                            extendedStyles={styles.erpNumberText}
                                        />
                                    </StyledWrapper>
                                    <ProductActions iconOnly={true} discardPrice={true} />
                                </StyledWrapper>
                            </StyledWrapper>
                        </ProductContext.Provider>
                    );
                })}
            </>
        );
    }
}

export default AutocompleteProducts;
